import connectApi from 'api/connectApi'
import { Button } from 'components/button/Button'
import {
    constants,
    generateUuidv4,
    getConnectUser,
    isUnknownVillageName,
    openClientFromSearch,
    PhoneLookupMatch,
    setOpenedVillageSession,
    sendMessageToWorkerWithResponse,
    VillageType
} from 'helpers'
import { internetConnect } from 'helpers/internetConnect'
import { useAppSelector, useBindDispatch } from 'hooks'
import { FarmerIdModal } from 'pages/farmerId/FarmerIdModal'
import React, { MutableRefObject, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClientType } from 'storeTypes'
import { Modal, Spinner, TimedAlert } from 'components'
import { ButtonStylesEnum } from 'types/buttonStylesEnum'

type OpenExistingClientArgs = {
    formCompletedRef: MutableRefObject<boolean>
    pathname: string
    canUseFarmerId: boolean
    onOpenedExisting: (source: 'core' | 'local') => void
    navigate: (to: string, opts?: { replace?: boolean }) => void
}

type VillageListState = {
    clientList?: ClientType[]
    previousClientList?: ClientType[]
}

const requestFailed = (result: unknown) => {
    if (!result || typeof result !== 'object') return false
    return (
        (result as { meta?: { requestStatus?: string } }).meta
            ?.requestStatus === 'rejected'
    )
}

const villageHasClients = (villageState?: VillageListState) =>
    (villageState?.clientList?.length || 0) +
        (villageState?.previousClientList?.length || 0) >
    0

const findVillageById = (
    villageId: string | undefined,
    lists: Array<VillageType[] | undefined>
) => {
    if (!villageId) return undefined
    for (const list of lists) {
        const match = list?.find((village) => village.sf_id === villageId)
        if (match) return match
    }
    return undefined
}

export const useOpenExistingClient = ({
    formCompletedRef,
    pathname,
    canUseFarmerId,
    onOpenedExisting,
    navigate
}: OpenExistingClientArgs) => {
    const { t } = useTranslation()
    const {
        selectedClientHandler,
        getClientOrdersHandler,
        villageClientListHandler,
        storeVillageOrdersHandler,
        selectVeVillageHandler
    } = useBindDispatch()
    const { clientOrders } = useAppSelector((state) => state.clientOrders)
    const assignedVillages = useAppSelector((state) => state.ves?.villages)
    const selectedVeVillages = useAppSelector(
        (state) => state.ve?.selectedVeVillages
    )
    const villageClientList = useAppSelector((state) => state.villageClientList)
    const [showFarmerIdModal, setShowFarmerIdModal] = useState(false)
    const [selectedClient, setSelectedClient] = useState<ClientType | null>(
        null
    )
    const [villageChoices, setVillageChoices] = useState<VillageType[]>([])
    const [isPreparingVillage, setIsPreparingVillage] = useState(false)
    const [openError, setOpenError] = useState('')
    const pendingClientRef = useRef<PhoneLookupMatch | null>(null)
    const villageChoiceRef = useRef<((opened: boolean) => void) | null>(null)
    const openedRef = useRef(false)
    const matchSourceRef = useRef<'core' | 'local'>('core')
    const preparingRef = useRef(false)

    const markProfileOpened = () => {
        if (openedRef.current) return
        openedRef.current = true
        formCompletedRef.current = true
        onOpenedExisting(matchSourceRef.current)
    }

    const switchToVillage = (village: VillageType) => {
        const current = getConnectUser().veVillages || []
        if (current.some((item) => item.sf_id === village.sf_id)) return
        const standalone = setOpenedVillageSession(village)
        selectVeVillageHandler(standalone)
    }

    const ensureVillageReady = async (village: VillageType) => {
        if (villageHasClients(villageClientList?.[village.sf_id])) {
            return true
        }
        const result = await villageClientListHandler({
            villageId: village.sf_id
        })
        if (requestFailed(result)) return false
        const payload = (
            result as {
                payload?: {
                    clientListData?: ClientType[]
                    prevClientListData?: ClientType[]
                }
            }
        ).payload
        const loaded =
            (payload?.clientListData?.length || 0) +
                (payload?.prevClientListData?.length || 0) >
            0
        if (!loaded && !internetConnect()) return false
        try {
            await storeVillageOrdersHandler([{ sf_id: village.sf_id }])
        } catch {
            // Profile can still open; packages may fill in later.
        }
        return true
    }

    const storeAndOpen = async (
        client: PhoneLookupMatch,
        villageId: string,
        villageName?: string
    ) => {
        const stored = {
            ...client,
            client_code: String(client.client_code),
            village_id: villageId,
            village: villageName || client.village
        } as ClientType
        selectedClientHandler({
            clientData: stored,
            clientType:
                (stored.all_enrollment || 0) <= 0 ? 'previous' : 'current'
        })
        await sendMessageToWorkerWithResponse({
            'store client': { villageId, client: stored }
        })
        openClientFromSearch(stored, {
            canUseFarmerId,
            pathname,
            navigate: (to, opts) => {
                markProfileOpened()
                navigate(to, opts)
            },
            onShowFarmerIdModal: (nextClient) => {
                setSelectedClient(nextClient)
                setShowFarmerIdModal(true)
            },
            getClientOrders: getClientOrdersHandler
        })
    }

    const assignVillage = async (
        client: PhoneLookupMatch,
        village: VillageType
    ) => {
        const { username } = getConnectUser()
        const response = await connectApi.post(
            constants.endpoints.CLIENT_DETAILS,
            {
                form_type: constants.FORM_TYPES.MODIFICATION,
                client_code: client.client_code,
                village: village.sf_id,
                uuid: generateUuidv4(),
                date: new Date().toISOString(),
                username
            }
        )
        if (!response || response.status >= 400) return false
        await storeAndOpen(client, village.sf_id, village.name)
        return true
    }

    const openAssignedVillage = async (
        client: PhoneLookupMatch,
        village: VillageType
    ) => {
        if (preparingRef.current) return false
        preparingRef.current = true
        setOpenError('')
        setIsPreparingVillage(true)
        try {
            const ready = await ensureVillageReady(village)
            if (!ready) {
                setOpenError(t('enrollment.openVillageFailed'))
                return false
            }
            switchToVillage(village)
            await storeAndOpen(
                client,
                village.sf_id,
                village.name || client.village
            )
            return true
        } catch {
            setOpenError(t('enrollment.openVillageFailed'))
            return false
        } finally {
            preparingRef.current = false
            setIsPreparingVillage(false)
        }
    }

    const openExistingClient = async (
        client: PhoneLookupMatch,
        source: 'core' | 'local' = 'core'
    ) => {
        matchSourceRef.current = source
        const villages = getConnectUser().veVillages || []
        const assignedVillage = findVillageById(client.village_id, [
            villages,
            assignedVillages,
            selectedVeVillages
        ])
        if (assignedVillage) {
            return openAssignedVillage(client, assignedVillage)
        }
        if (isUnknownVillageName(client.village)) {
            if (villages.length === 1) {
                return assignVillage(client, villages[0])
            }
            if (villages.length > 1) {
                pendingClientRef.current = client
                setVillageChoices(villages)
                villageChoiceRef.current?.(false)
                return new Promise<boolean>((resolve) => {
                    villageChoiceRef.current = resolve
                })
            }
            return false
        }
        return false
    }

    const chooseVillage = async (village: VillageType) => {
        const client = pendingClientRef.current
        if (!client) return
        const assigned = await assignVillage(client, village)
        if (assigned) settleVillageChoice(true)
    }

    const settleVillageChoice = (opened: boolean) => {
        pendingClientRef.current = null
        setVillageChoices([])
        const settle = villageChoiceRef.current
        villageChoiceRef.current = null
        settle?.(opened)
    }

    const cancelVillageChoice = () => {
        settleVillageChoice(false)
    }

    const existingClientModals = (
        <>
            {isPreparingVillage && (
                <Spinner size="90" pageSpinner fullscreen />
            )}
            {openError ? (
                <TimedAlert type="floating" status="error">
                    {openError}
                </TimedAlert>
            ) : null}
            <Modal
                showModal={villageChoices.length > 0}
                position={'center-expandable'}
                hideButtons
                showCloseIconBtn
                closeClickHandler={cancelVillageChoice}
            >
                <h2 className="modal--header">
                    {t('enrollment.chooseVillage')}
                </h2>
                {villageChoices.map((village) => (
                    <Button
                        key={village.sf_id}
                        style={ButtonStylesEnum.PRIMARY}
                        onClick={() => chooseVillage(village)}
                    >
                        {village.name}
                    </Button>
                ))}
                <Button
                    style={ButtonStylesEnum.OUTLINE}
                    onClick={cancelVillageChoice}
                    ariaLabel="village-choice-cancel"
                >
                    {t('enrollment.return')}
                </Button>
            </Modal>
            <Modal
                showModal={showFarmerIdModal && Boolean(selectedClient)}
                data-testid="farmer-id-modal"
                position={'center'}
                className="farmer-id--modal"
                closeClickHandler={() => setShowFarmerIdModal(false)}
                showCloseIconBtn
            >
                {selectedClient && (
                    <FarmerIdModal
                        clientData={selectedClient}
                        selectClientHandler={selectedClientHandler}
                        closeModalHandler={() => setShowFarmerIdModal(false)}
                        clientOrders={clientOrders || []}
                        returnPath={pathname}
                        onLeaveForm={markProfileOpened}
                    />
                )}
            </Modal>
        </>
    )

    return { openExistingClient, existingClientModals }
}
